import { ApiKeyFormModal } from '@/components/api-keys/ApiKeyFormModal';
import { RevealSecretDialog } from '@/components/api-keys/RevealSecretDialog';
import { UsageTable } from '@/components/api-keys/UsageTable';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import type { ApiKey, ApiKeyWithSecret } from '@innermate/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Ban, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function UserApiKeys() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground-muted text-sm">
        로딩 중…
      </div>
    );
  }

  const allowed = user.apiRelayEnabled && user.llmApiRelayEnabledGlobal;

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-10 border-b border-border bg-background">
          <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: '/chat' })}
                aria-label="뒤로"
              >
                <ArrowLeft className="size-4" />
                채팅으로
              </Button>
              <h1 className="text-base font-semibold">API 관리</h1>
            </div>
            <span className="text-xs text-foreground-muted">{user.email}</span>
          </div>
        </header>
        <main className="mx-auto max-w-5xl px-6 py-8">
          {allowed ? <UserApiKeysContent /> : <NotAllowedNotice />}
        </main>
      </div>
    </ThemeProvider>
  );
}

function NotAllowedNotice() {
  return (
    <section className="rounded-2xl border border-border bg-surface p-8 text-center">
      <h2 className="text-lg font-semibold">API 키 발급 권한이 없습니다</h2>
      <p className="mt-2 text-sm text-foreground-muted">
        관리자에게 문의해 LLM API 릴레이 권한을 부여받으세요.
      </p>
    </section>
  );
}

function UserApiKeysContent() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [reveal, setReveal] = useState<ApiKeyWithSecret | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['api-keys', 'list'],
    queryFn: () => api.apiKeysList(),
  });

  const usageQuery = useInfiniteQuery({
    queryKey: ['api-keys', 'usage'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.apiKeysUsage({ cursor: pageParam, limit: 50 }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => api.apiKeyRevoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys', 'list'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '취소에 실패했습니다.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.apiKeyDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys', 'list'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    },
  });

  const keys = listQuery.data?.keys ?? [];
  const usageItems = useMemo(
    () => usageQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [usageQuery.data],
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          usageQuery.hasNextPage &&
          !usageQuery.isFetchingNextPage
        ) {
          usageQuery.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [usageQuery]);

  return (
    <div className="space-y-8">
      {serverError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
          <button
            type="button"
            className="ml-3 text-xs underline"
            onClick={() => setServerError(null)}
          >
            닫기
          </button>
        </div>
      ) : null}

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold">내 API 키</h2>
            <p className="text-sm text-foreground-muted">
              발급된 키로 OpenAI 호환 클라이언트 (curl, OpenAI SDK 등) 에서 LLM 을 호출할 수
              있습니다.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            발급
          </Button>
        </div>

        {listQuery.isLoading ? (
          <p className="text-sm text-foreground-muted">불러오는 중…</p>
        ) : (
          <KeyTable
            keys={keys}
            onRevoke={(id) => revokeMut.mutate(id)}
            onDelete={(id) => {
              if (window.confirm('정말 삭제할까요? 사용량 기록은 보존되지만 키는 사라집니다.')) {
                deleteMut.mutate(id);
              }
            }}
          />
        )}
      </section>

      <section>
        <h2 className="text-base font-semibold mb-3">사용량</h2>
        <UsageTable items={usageItems} showUser={false} />
        <div ref={sentinelRef} className="h-8" />
        {usageQuery.isFetchingNextPage ? (
          <p className="mt-2 text-center text-xs text-foreground-muted">불러오는 중…</p>
        ) : null}
      </section>

      <ApiKeyFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        variant="user"
        onCreated={(k) => setReveal(k)}
      />
      <RevealSecretDialog open={!!reveal} onClose={() => setReveal(null)} secretKey={reveal} />
    </div>
  );
}

function KeyTable({
  keys,
  onRevoke,
  onDelete,
}: {
  keys: ApiKey[];
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
          <tr>
            <Th>이름</Th>
            <Th>접두어</Th>
            <Th>만료</Th>
            <Th>마지막 사용</Th>
            <Th>상태</Th>
            <Th align="right">액션</Th>
          </tr>
        </thead>
        <tbody>
          {keys.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-foreground-muted py-10">
                발급된 키가 없습니다. '발급' 버튼으로 시작하세요.
              </td>
            </tr>
          ) : (
            keys.map((k) => (
              <tr key={k.id} className="border-t border-border hover:bg-surface-muted/30">
                <td className="px-4 py-3">
                  <div className="font-medium">{k.name}</div>
                  <div className="text-[11px] text-foreground-muted">
                    발급 {new Date(k.createdAt).toLocaleDateString('ko-KR')}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{k.keyPrefix}…</td>
                <td className="px-4 py-3 text-xs">
                  {k.expiresAt ? new Date(k.expiresAt).toLocaleString('ko-KR') : '영구'}
                </td>
                <td className="px-4 py-3 text-xs">
                  {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString('ko-KR') : '—'}
                </td>
                <td className="px-4 py-3">
                  <KeyStatusBadge k={k} />
                </td>
                <td className="px-4 py-3 text-right">
                  <KeyMenu
                    isRevoked={!!k.revokedAt}
                    onRevoke={() => onRevoke(k.id)}
                    onDelete={() => onDelete(k.id)}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function KeyStatusBadge({ k }: { k: ApiKey }) {
  if (k.revokedAt) {
    return (
      <span className="inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider bg-destructive/15 text-destructive">
        취소됨
      </span>
    );
  }
  if (k.expiresAt && new Date(k.expiresAt).getTime() < Date.now()) {
    return (
      <span className="inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-600">
        만료됨
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider bg-success/15 text-success">
      활성
    </span>
  );
}

function KeyMenu({
  isRevoked,
  onRevoke,
  onDelete,
}: { isRevoked: boolean; onRevoke: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, right: pos.right }}
              className="w-44 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm text-left"
            >
              <button
                type="button"
                disabled={isRevoked}
                onClick={() => {
                  setOpen(false);
                  onRevoke();
                }}
                className={cn(
                  'flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
                  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
                )}
              >
                <Ban className="size-4" />
                {isRevoked ? '이미 취소됨' : '취소'}
              </button>
              <div className="my-1 border-t border-border" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted text-destructive"
              >
                <Trash2 className="size-4" />
                삭제
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
